
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { formatCurrency } from 'lib/helpers/strings';
import SEO from 'modules/App/components/SEO';
import AvailableProfile from 'modules/App/data-components/AvailableProfile';
import DataResponseContainer from 'modules/common/components/DataResponseContainer';
import Modal from 'modules/common/components/Modal';
import AsideLayout from 'modules/common/layouts/AsideLayout';
import { gql, useMutation, useQuery } from 'modules/GraphQL';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Tooltip from 'rc-tooltip';
import React from 'react';
import { track } from '@/lib/analytics/client';
import classNames from 'classnames';
import dayjs from 'dayjs';
import HeadHunterAvatar from 'modules/App/components/HeadHunterAvatar';
import Member from 'modules/App/data-components/Member';
import { withAuth } from 'modules/auth/server';
import EmptyState from 'modules/common/components/EmptyState';
import SearchItems, { useSearchFilterQuery } from 'modules/common/components/SearchItems';
import Table, { ColumnDef } from 'modules/common/components/Table';
import AnimatedGradientText from 'modules/common/components/ui/animated-gradient-text';
import useUrl from 'modules/common/hooks/useUrl';
import Areas from 'modules/geo/components/Areas';
import CountryEmojis from 'modules/geo/data-components/CountryEmojis';
import { Link } from 'modules/i18n';
import useNotificationCenter from 'modules/notification-center/hooks/useNotificationCenter';
import useUser from 'modules/User/hooks/useUser';
import { useQueryState } from 'nuqs';
import { MdSearchOff as IconNoData, MdOutlineArrowForwardIos as IconView, MdFilterList } from 'react-icons/md';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/components/ui/popover';
import { Matching } from 'modules/App/models/Matching';
import MatchingCard from 'modules/App/components/MatchingCard';
import { Badge } from '@/lib/components/ui/badge';
import BountyRecommandation from 'modules/App/components/BountyRecommandation';
import { MdPersonSearch as IconHighlightedOpenToSource } from 'react-icons/md';
import { RxStarFilled as IconHighlighted } from 'react-icons/rx';
import UserStats from 'modules/App/components/UserStats';
import { Loading } from 'lib/components/ui/loading';
import { cn } from '@/lib/utils';
const experiences: any = {
    '<2': '0 à 2 ans',
    '2/4': '2 à 4 ans',
    '3/5': '3 à 5 ans',
    '5/8': '5 à 8 ans',
    '8/10': '8 à 10 ans',
    '>10': '> 10 ans',
};
const CREATE_CONTACT = gql `
  # Increments a back-end counter and gets its resulting value
  mutation CreateContactMissionAvailableProfile($input: createContactMissionInput!) {
    createContactMission(input: $input) {
      item {
        _id
      }
    }
  }
`;
const LIST_AVAILABLE_PROFILES = gql `
  query AvailableProfiles($sort: String, $filter: AvailableProfilesFilterInput) {
    availableProfiles(sort: $sort, limit: 1000, filter: $filter) {
      pageInfo {
        count
        page
        limit
      }
      items {
        _id
        name
        bounty
        sector
        sectorIds
        occupation
        occupationIds
        job
        client
        location
        experience
        baseSalary {
          min
          max
        }
        description
        context
        createdBy {
          _id
          name
          title
          image
          isTop
        }
        createdAt
        updatedAt
        expiresAt
        contacts {
          status
          inProcess
          endedProcess
          processSucceeded
          processConfirmed
          contactStopped
          candidateUnderOfferAt
          contactInitiated
        }
        myMatchings {
          _id
          viewedAt
          searchedProfileUserId
          searchedProfileId
          searchedProfile {
            _id
            name
            bounty
            baseSalary {
              min
              max
            }
            currency
            experience
            publishedAt

            sectorIds
            countryIds
            occupationId
          }
        }
        countryIds
        areaSearch {
          areas {
            name
            radius
          }
        }
        sectors {
          name
        }
        currency
      }
    }
  }
`;
const AvailableProfilesPage = () => {
    const router = useRouter();
    const selectedId = ((router.query?.slug || [])[0] || router.query._id) as string;
    const { queryParams: { page = 1, limit = 200, sort }, pushQueryParam, } = useUrl();
    const searchParamsString = router.asPath.split('?')[1] ? `?${router.asPath.split('?')[1]}` : '';
    const [from, setFrom] = useQueryState('from');
    const { markNotificationAsViewed } = useNotificationCenter();
    const { protect, hasPermission, user } = useUser();
    const { filter: filterQuery, setQ } = useSearchFilterQuery();
    const [createContactMission] = useMutation(CREATE_CONTACT);
    const [memberId, toggleMember] = React.useState<string | undefined>();
    const { loading, error, data, refetch } = useQuery(LIST_AVAILABLE_PROFILES, { variables: { sort, filter: filterQuery } });
    // React.useEffect(() => {
    //   refetch({ sort, filter: { sectorIds, countryIds } });
    // }, [refetch, sort, sectorIds, countryIds]);
    const { items: availableProfiles = [], pageInfo = {} } = data?.availableProfiles || {};
    const count = pageInfo?.count || 0;
    const closeModal = () => {
        router.push(`/available-profiles${searchParamsString}`);
    };
    const details = protect('member')((itemId: string) => {
        router.push(`/available-profiles/${itemId}${searchParamsString}`);
    });
    const proposeMission = (itemId: string) => async ({ showHeadhunter }: {
        showHeadhunter?: boolean;
    } = {}) => {
        const { data } = await createContactMission({ variables: { input: { availableProfile: itemId } } });
        const item = data?.createContactMission?.item;
        if (item) {
            router.push(`/contact/mission/${item._id}${showHeadhunter ? '?headhunter=true' : ''}`);
        }
    };
    const filterProps = (value: any, className?: string) => ({
        onClick: (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            setQ(value);
        },
        className: `${className || ''} hover:opacity-70 hover:cursor-zoom-in`,
    });
    const selectedAvailableProfile = availableProfiles.find(({ _id }: any) => _id === selectedId);
    React.useEffect(() => {
        if (from && selectedAvailableProfile) {
            const [source, notificationId] = from.split('_');
            if (notificationId)
                markNotificationAsViewed({ _id: notificationId });
            track(`available_profile.viewed.from.${source}`, selectedAvailableProfile);
            setFrom(null);
        }
    }, [selectedAvailableProfile, from, setFrom]);
    const columns = React.useMemo<ColumnDef<any>[]>(() => [
        {
            header: 'Profil du candidat',
            accessorKey: 'name',
            cell: (info) => {
                const { name, client, countryIds, areaSearch } = info.row.original;
                return (<div className="w-56 max-w-[33vw]">
              <div {...filterProps(name, '2xl:w-max whitespace-pre-wrap sm:whitespace-normal max-w-full font-bold truncate')}>{name}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <CountryEmojis countryIds={countryIds}/>
                <Areas items={areaSearch?.areas || []}/>
                <small className="italic">{client || 'Client confidentiel'}</small>
              </div>
            </div>);
            },
        },
        {
            header: () => (<>
            Prime HT
            <br />
            Hors frais HL
          </>),
            align: 'left',
            sortBy: ['bounty'],
            accessorKey: 'bounty',
            cell: (info) => {
                const { bounty, currency, baseSalary, urgent, bountyDescription } = info.row.original;
                return (<>
              <BountyRecommandation bounty={bounty} salaryMin={baseSalary.min} salaryMax={baseSalary.max} isUrgent={urgent} bountyDescription={bountyDescription}>
                <strong>{formatCurrency({ locale: user?.locale, value: bounty, precision: 2, currency })}</strong>
              </BountyRecommandation>
            </>);
            },
        },
        {
            header: 'Contacts',
            accessorKey: 'updatedAt',
            cell: (info) => {
                const { contacts, createdAt, updatedAt, myMatchings: matchings, _id, sectors = [], occupations = [], sectorIds, occupationIds, countryIds, name, } = info.row.original;
                const contactsDone = contacts.filter(({ status }: {
                    status: string;
                }) => status !== 'draft');
                const contactsInProcess = contacts.filter(({ inProcess }: {
                    inProcess: boolean;
                }) => inProcess);
                const contactsStopped = contacts.filter(({ endedProcess, contactStopped }: {
                    endedProcess: boolean;
                    contactStopped: boolean;
                }) => endedProcess || contactStopped);
                const contactsInitiated = contacts.filter(({ contactInitiated }: {
                    contactInitiated: boolean;
                }) => contactInitiated);
                const contactProcessConfirmed = contacts.some(({ processConfirmed }: {
                    processConfirmed: boolean;
                }) => processConfirmed);
                const contactProcessConfirmedAt = contacts
                    .filter(({ candidateUnderOfferAt }: any) => candidateUnderOfferAt)
                    .reduce((earliest: number, { candidateUnderOfferAt }: any) => !earliest || dayjs(candidateUnderOfferAt).isBefore(dayjs(earliest)) ? candidateUnderOfferAt : earliest, 0 as number);
                const difference = dayjs(contactProcessConfirmedAt).diff(dayjs(createdAt), 'day');
                const trackProperties = {
                    availableProfileId: _id,
                    name,
                    sectorIds,
                    occupationIds,
                    countryIds,
                    sectorNames: sectors.map(({ name }: any) => name),
                    occupationNames: occupations.map(({ name }: any) => name),
                };
                const Matchings = matchings.length > 0 ? (<Popover>
                <PopoverTrigger asChild onClick={(e: any) => {
                        e.stopPropagation();
                        track('available_profiles.click.matchings', trackProperties);
                    }}>
                  <div className={classNames('text-center leading-none shadow-sm border rounded-lg cursor-pointer p-1 my-2', {
                        'bg-accent border-accent hover:bg-accent/80 text-white': matchings.length > 0,
                    })}>
                    <strong>{matchings.length}</strong> de tes missions correspond{matchings.length > 1 ? 'ent' : ''}
                  </div>
                </PopoverTrigger>
                <PopoverContent side="bottom" sticky="always" className="py-4 px-2 z-20 flex flex-col gap-2 overflow-y-auto max-h-[35vh] bg-gray-100">
                  {matchings.length > 0 ? (<>
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        <span>Mission{matchings.length > 1 ? 's' : ''} à proposer</span>
                        <Badge variant="ghost">{matchings.length}</Badge>
                      </h2>
                      <p className="text-xs text-gray-500 mb-4 max-w-[400px]">
                        Voici certaines de tes missions qui peuvent correspondre à ce candidat.
                      </p>
                    </>) : null}
                  {matchings.map((matching: Matching) => (<MatchingCard key={matching._id} matching={matching} type="searchedProfile" onPropose={proposeMission(_id)} action="propose" onStatusChange={() => refetch()}/>))}
                </PopoverContent>
              </Popover>) : null;
                if (contactProcessConfirmed) {
                    return (<AnimatedGradientText className="text-center text-sm max-w-none rounded-lg" variant="success">
                <span className={classNames(`inline  bg-[length:var(--bg-size)_50%] `)}>
                  <span className="">🎉</span> Candidat sous offre 🎉
                  <small className="whitespace-nowrap block">en {difference} jours</small>
                </span>
              </AnimatedGradientText>);
                }
                if (contactsDone.length === 0) {
                    return (<>
                <AnimatedGradientText className="text-center text-sm max-w-none rounded-lg">
                  <span className={classNames(`inline animate-gradient bg-gradient-to-r from-primary via-accent to-primary bg-[length:var(--bg-size)_100%] bg-clip-text text-transparent leading-none`)}>
                    Prêt pour interview
                  </span>
                </AnimatedGradientText>
                {Matchings}
              </>);
                }
                return (<>
              <div className="leading-tight text-xs mx-auto rounded-lg bg-white/40 border border-dashed border-gray-500 px-4 py-1.5">
                <div className="whitespace-nowrap">
                  Mission{contactsDone.length > 1 ? 's' : ''} proposée{contactsDone.length > 1 ? 's' : ''} : <strong>{contactsDone.length}</strong>
                </div>
                <div className="flex items-center gap-1 flex-wrap mt-1">
                  <Badge variant="outline-ghost" size="sm" className={cn('whitespace-nowrap tracking-tight', contactsInitiated.length === 0 && 'opacity-50')}>
                    à l’étude: <strong>{contactsInitiated.length}</strong>
                  </Badge>
                  <Badge variant="outline-primary" size="sm" className={cn('whitespace-nowrap tracking-tight', contactsInProcess.length === 0 && 'opacity-50')}>
                    en process: <strong>{contactsInProcess.length}</strong>
                  </Badge>
                  {contactsStopped.length > 0 && (<Badge variant="outline-destructive" size="sm" className={'whitespace-nowrap tracking-tight'}>
                      rejeté{contactsStopped.length > 1 ? 's' : ''}: <strong>{contactsStopped.length}</strong>
                    </Badge>)}
                </div>
                <time className="block text-faded text-xs mt-1 italic text-right" dateTime={dayjs(updatedAt).format()}>
                  {dayjs(updatedAt).fromNow()}
                </time>
              </div>
              {Matchings}
            </>);
            },
        },
        {
            header: () => (<>
            Secteur
            <br />
            <small>Métier</small>
          </>),
            accessorKey: 'sector',
            cell: (info) => {
                const { sector, occupation } = info.row.original;
                return (<>
              <span {...filterProps(sector)} className="block max-w-[200px] truncate text-xs font-semibold">
                <Tooltip placement="top" trigger={['click', 'hover']} overlayClassName="opacity-100" overlay={sector}>
                  <span>{sector}</span>
                </Tooltip>
              </span>
              {occupation && (<Tooltip placement="top" trigger={['click', 'hover']} overlayClassName="opacity-100" overlay={occupation}>
                  <small className="block max-w-[200px] truncate text-xs font-normal">{occupation}</small>
                </Tooltip>)}
            </>);
            },
        },
        {
            header: () => (<>
            Expérience
            <br />
            Salaire (Pack.)
          </>),
            accessorKey: 'experience',
            sortBy: ['baseSalary.max'],
            cell: (info) => {
                const { experience, baseSalary, currency } = info.row.original;
                return (<>
              <small className="block">
                Exp: <strong>{experiences[experience]}</strong>
              </small>
              <small className="block">
                {formatCurrency({ locale: 'fr', value: +baseSalary.min, precision: 2, compact: true, currency })} -{' '}
                {formatCurrency({ locale: 'fr', value: +baseSalary.max, precision: 2, compact: true, currency })}
              </small>
            </>);
            },
        },
        {
            header: () => (<>
            Candidat
            <br />
            Publié par
          </>),
            accessorKey: 'createdBy.name',
            cell: (info) => {
                const { createdBy, updatedAt } = info.row.original;
                return (<>
              <Tooltip placement="top" trigger={['click', 'hover']} overlayClassName="opacity-100" overlay={<>
                    <div className="rounded-lg bg-white overflow-hidden shadow">
                      <h2 className="sr-only" id="profile-overview-title">
                        Profile Overview
                      </h2>
                      <div className="bg-white p-2">
                        <div className="sm:flex sm:items-center sm:justify-between sm:space-x-2">
                          <div className="shrink-0">
                            {createdBy?.image && <Image className="mx-auto rounded-full" width={128} height={128} src={createdBy?.image} alt=""/>}
                          </div>
                          <div className="mt-4 text-center sm:mt-0 sm:pt-1 sm:text-left max-w-xs">
                            <p className="text-xl font-bold text-gray-900 sm:text-xl">{createdBy?.name}</p>
                            <p className="text-sm font-medium text-gray-600">{createdBy?.title}</p>
                            {createdBy?.isTop && (<div>
                                <Badge variant="outline-primary" className="mt-1">
                                  <IconHighlighted className="text-primary h-4 w-4 stroke-white stroke-1"/> Top Headlinker
                                </Badge>
                              </div>)}
                            {createdBy?.openToSource && (<div>
                                <Badge variant="outline-accent" className="mt-1">
                                  <IconHighlightedOpenToSource className="text-accent h-4 w-4 stroke-white stroke-1"/> Dispo pour sourcer !
                                </Badge>
                              </div>)}
                          </div>
                        </div>
                      </div>
                      <UserStats user={createdBy} className="p-2 leading-relaxed"/>
                      <div className="border-t border-gray-200 bg-gray-50 grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-2 sm:divide-y-0 sm:divide-x">
                        <div className="px-2 py-2 text-sm font-medium text-center">
                          <button onClick={(e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleMember(createdBy._id);
                        }} className="btn btn-primary btn-xs btn-outline">
                            Contactez
                          </button>
                        </div>
                        <div className="px-2 py-2 text-sm font-medium text-center">
                          <button onClick={(e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setQ(createdBy?.name);
                        }} className="btn btn-xs btn-outline">
                            Voir ses missions
                          </button>
                        </div>
                      </div>
                    </div>
                  </>}>
                <div className="flex items-center gap-2">{createdBy?.name && <HeadHunterAvatar user={createdBy}/>}</div>
              </Tooltip>
              <small className="block italic text-[10px] text-faded">
                <time dateTime={updatedAt} title={dayjs(updatedAt).format('llll')} suppressHydrationWarning>
                  {dayjs(updatedAt).fromNow()}
                </time>
              </small>
            </>);
            },
        },
        {
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const { _id, expiresAt } = info.row.original;
                return (<>
              <button className="btn btn-primary btn-sm gap-1 w-auto sm:w-24" onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        details(_id);
                    }}>
                <IconView />
                <span className="hidden sm:inline-block">Voir</span>
              </button>
              {dayjs().isBefore(dayjs(expiresAt)) && hasPermission('superadmin') && (<small className="block" title="Seulement visible pour les admins">
                  Expire{' '}
                  <span className={classNames('font-bold', {
                            'text-warning': dayjs().isAfter(dayjs(expiresAt).add(-8, 'days')),
                            'text-error': dayjs().isAfter(dayjs(expiresAt).add(-1, 'days')),
                        })}>
                    {dayjs(expiresAt).fromNow()}
                  </span>
                </small>)}
            </>);
            },
        },
    ], [details, hasPermission]);
    const mySearch = selectedAvailableProfile?.createdBy?._id?.toString() === user?.id?.toString();
    return (<AsideLayout>
      <SEO title={[selectedAvailableProfile?.name, `Candidats Off-Market`].filter(Boolean).join(' - ')}/>
      <Modal onClose={closeModal} open={!!selectedId} title={'Détail'} className="p-2 max-w-screen-lg">
        {selectedId && (<div className="text-left w-full p-2 sm:p-8">
            <AvailableProfile className="text-md sm:text-lg" _id={selectedId} withTitle={true} withFees={true} openDescription mySearch={mySearch} onProposeMission={proposeMission(selectedId)}/>
          </div>)}
      </Modal>
      <Modal onClose={() => toggleMember(undefined)} open={!!memberId} title={'Détail'} className="p-2 max-w-screen-lg">
        {memberId && (<div className="text-left w-full p-2 md:p-8">
            <Member className="text-md md:text-lg" _id={memberId} openDescription={true}/>
          </div>)}
      </Modal>

      <div>
        <h1 className="flex items-center justify-start gap-2">
          <span>CVThèque Off-Market</span>
          <small className="badge badge-ghost">{loading ? <Loading size="xs"/> : availableProfiles.length}</small>
        </h1>
        <p className="text-sm -my-2 mb-2">
          Candidats mis à jour il y a moins de <strong>60 jours</strong> et sourcés par un professionnel du recutement (comme toi)
        </p>
        <div className="flex items-center gap-2 bg-gray-50 px-1 sm:px-2">
          <MdFilterList className="h-7 w-7 "/>
          <SearchItems placeholder="Recherche" className="my-3 mx-2"/>
        </div>
        <DataResponseContainer loading={loading} error={error} data={availableProfiles} loadingComponent={<Table columns={columns} data={[]} onChangePage={pushQueryParam('page')} onChangeLimit={pushQueryParam('limit')} onChangeSort={pushQueryParam('sort')} loading={true} page={+page} limit={+limit} sort={sort} count={count} pageSizes={[20, 50, 100, 200]}/>}>
          {!loading && availableProfiles?.length === 0 ? (<EmptyState className="mt-5" title={Object.keys(filterQuery).length > 0
                ? 'Aucun candidat disponible ne correspond à ta recherche'
                : 'Aucun candidat disponible ne correspond à ton profil, élargis tes filtres pour obtenir des résultats'} subtitle={availableProfiles.length > 0 ? ('Aucun candidat ne correspond à ta recherche') : (<>
                    Aide-nous en{' '}
                    <Link href="/affiliate" className="link link-primary">
                      parrainant tes pairs
                    </Link>
                  </>)} cta="Recommencer" onClick={() => setQ('')} icon={IconNoData}/>) : (<Table className="table table-compact table-zebra w-full" columns={columns} data={availableProfiles} onChangePage={pushQueryParam('page')} onChangeLimit={pushQueryParam('limit')} onChangeSort={pushQueryParam('sort')} onRowClick={({ _id }) => details(_id)} loading={loading} page={+page} limit={+limit} sort={sort} count={count} pageSizes={[20, 50, 100, 200]}/>)}
        </DataResponseContainer>
      </div>
    </AsideLayout>);
};
const getServerSideProps = withAuth();
export default AvailableProfilesPage;

    async function __Next_Translate__getServerSideProps__1989496f502__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/available-profiles/[[...slug]]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1989496f502__ as getServerSideProps }
  